import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import EmailTemplateService, { DEFAULT_TEMPLATE_VARIABLES, TEMPLATE_CATEGORIES } from '../services/EmailTemplateService';

const EmailTemplateEditor = ({ 
  template = null, 
  onSave, 
  onCancel, 
  isOpen = false,
  invoiceData = null 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: 'general',
    description: ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showVariables, setShowVariables] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const textAreaRef = useRef(null);

  // Initialize form data when template changes
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        content: template.content || '',
        category: template.category || 'general',
        description: template.description || ''
      });
    } else {
      setFormData({
        name: '',
        content: `Hi {firstName},

I hope this email finds you well! I'm excited to share that your invoice for "{projectName}" is now ready.

**Invoice Details:**
• Project: {projectName}
• Amount: {amount}
• Due Date: {dueDate}
• Invoice #: {invoiceNumber}

{paymentLink}

Thank you for choosing us for your special day. If you have any questions about this invoice, please don't hesitate to reach out.

Looking forward to working with you!

Best regards,
{userName}
{businessName}`,
        category: 'general',
        description: ''
      });
    }
    setErrors({});
  }, [template]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle drag over text area
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  // Handle drop in text area
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const variable = e.dataTransfer.getData('text/plain');
    if (variable && textAreaRef.current) {
      const textArea = textAreaRef.current;
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;
      
      const newContent = formData.content.substring(0, start) + variable + formData.content.substring(end);
      
      setFormData(prev => ({
        ...prev,
        content: newContent
      }));
      
      // Reset cursor position
      setTimeout(() => {
        textArea.focus();
        textArea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  // Insert variable into template content
  const insertVariable = (variableKey) => {
    const variable = `{${variableKey}}`;
    const textArea = textAreaRef.current;
    if (!textArea) return;
    
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    
    const newContent = formData.content.substring(0, start) + variable + formData.content.substring(end);
    
    setFormData(prev => ({
      ...prev,
      content: newContent
    }));
    
    // Reset cursor position
    setTimeout(() => {
      textArea.focus();
      textArea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Template content is required';
    }
    
    if (formData.content.length > 2000) {
      newErrors.content = 'Template content is too long (max 2000 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save template
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const templateData = {
        ...formData,
        variables: EmailTemplateService.extractVariables(formData.content)
      };
      
      let savedTemplate;
      if (template?.id) {
        savedTemplate = await EmailTemplateService.updateTemplate(template.id, templateData);
        toast.success('Template updated successfully!');
      } else {
        savedTemplate = await EmailTemplateService.createTemplate(templateData);
        toast.success('Template created successfully!');
      }
      
      onSave?.(savedTemplate);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate preview data from invoice data or use defaults
  const getPreviewData = () => {
    if (invoiceData) {
      return {
        clientName: invoiceData.client || 'John Doe',
        firstName: invoiceData.client?.split(' ')[0] || 'John',
        projectName: invoiceData.project || 'Wedding Photography',
        amount: invoiceData.currency && invoiceData.amount ? `${invoiceData.currency} ${invoiceData.amount}` : '$2,500.00',
        totalAmount: invoiceData.currency && invoiceData.amount ? `${invoiceData.currency} ${invoiceData.amount}` : '$2,500.00',
        dueDate: invoiceData.dueDate ? new Date(invoiceData.dueDate).toLocaleDateString() : 'December 15, 2024',
        currency: invoiceData.currency || 'USD',
        userName: 'Your Name',
        businessName: 'Your Photography Business',
        invoiceNumber: 'INV-001',
        currentDate: new Date().toLocaleDateString(),
        paymentLink: '[View Invoice Button]'
      };
    }
    return {};
  };

  // Generate template preview with clean text (no HTML tags visible)
  const generatePreview = () => {
    const previewContent = EmailTemplateService.generatePreview(formData.content, getPreviewData());
    // Strip HTML tags for display but preserve line breaks
    return previewContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-[#111111] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-[#1A1A1A] shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {template ? 'Edit Template' : 'Create Template'}
            </h2>
            <p className="text-gray-600 dark:text-[#666666] mt-1">
              {template ? 'Update your email template' : 'Create a new custom email template'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="w-10 h-10 rounded-full bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#333333] flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Editor */}
          <div className="flex-1 flex flex-col">
            {/* Template Info */}
            <div className="p-6 border-b border-gray-200 dark:border-[#1A1A1A] space-y-4 shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Wedding Invoice Email"
                    className={`w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200 ${
                      errors.name ? 'border-red-500' : 'border-gray-200 dark:border-[#1A1A1A]'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white transition-all duration-200"
                  >
                    {TEMPLATE_CATEGORIES.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of when to use this template"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200"
                />
              </div>
            </div>

            {/* Template Editor */}
            <div className="flex-1 p-6 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-900 dark:text-white">
                  Template Content
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowVariables(!showVariables)}
                    className="px-3 py-1.5 bg-brand text-white text-sm font-medium rounded-lg hover:bg-[#E6A15E] transition-colors"
                  >
                    {showVariables ? 'Hide' : 'Add'} Variables
                  </button>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="px-3 py-1.5 bg-gray-100 dark:bg-[#1A1A1A] text-gray-900 dark:text-white text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
                  >
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                </div>
              </div>

              {/* Variables Panel - Compact */}
              <AnimatePresence>
                {showVariables && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mb-4 p-4 bg-gray-50 dark:bg-[#0A0A0A] rounded-xl border border-gray-200 dark:border-[#1A1A1A]"
                  >
                    <p className="text-sm text-gray-600 dark:text-[#666666] mb-3">
                      💡 Drag these into your email or click to insert at cursor
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {DEFAULT_TEMPLATE_VARIABLES.map(variable => (
                        <motion.div
                          key={variable.key}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', `{${variable.key}}`);
                          }}
                          onClick={() => insertVariable(variable.key)}
                          className="cursor-pointer px-3 py-1.5 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333333] rounded-lg text-sm text-gray-700 dark:text-white hover:border-brand hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-all duration-200 select-none"
                          whileHover={{ scale: 1.05 }}
                          whileDrag={{ scale: 1.1, rotate: 2 }}
                          title={variable.description}
                        >
                          {variable.label}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Content Editor */}
              <div className="flex-1 flex flex-col">
                <div
                  className={`relative transition-all duration-200 ${
                    isDragOver ? 'ring-2 ring-brand ring-offset-2' : ''
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <textarea
                    ref={textAreaRef}
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder="Start typing your email content here..."
                    className={`flex-1 w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200 resize-none font-mono text-sm min-h-[300px] ${
                      errors.content ? 'border-red-500' : 'border-gray-200 dark:border-[#1A1A1A]'
                    }`}
                  />
                  
                  {/* Subtle drop indicator */}
                  {isDragOver && (
                    <div className="absolute inset-0 bg-brand/5 rounded-xl pointer-events-none flex items-center justify-center">
                      <div className="bg-brand text-white px-3 py-1 rounded-lg text-sm font-medium">
                        Drop variable here
                      </div>
                    </div>
                  )}
                </div>
                
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">{errors.content}</p>
                )}
                
                {/* Character count */}
                <div className="flex justify-between items-center mt-2 text-sm text-gray-500 dark:text-[#666666]">
                  <span>Variables: {EmailTemplateService.extractVariables(formData.content).length}</span>
                  <span className={formData.content.length > 2000 ? 'text-red-500' : ''}>
                    {formData.content.length}/2000 characters
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <AnimatePresence>
            {showPreview && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '400px', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                className="border-l border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0A0A0A] overflow-hidden"
              >
                <div className="p-6 h-full flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Preview
                  </h3>
                  <div className="flex-1 bg-white dark:bg-[#1A1A1A] rounded-xl p-4 border border-gray-200 dark:border-[#333333] overflow-y-auto">
                    <div className="text-sm text-gray-600 dark:text-[#666666] mb-3">
                      Subject: Invoice for {getPreviewData().projectName || 'Your Project'}
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                      {formData.content ? generatePreview() : 'Start typing your template content to see the preview...'}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-[#1A1A1A] shrink-0">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-[#666666]">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Drag variables from above or click to insert them
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-100 dark:bg-[#1A1A1A] text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-6 py-2 bg-brand text-white rounded-lg hover:bg-[#E6A15E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmailTemplateEditor; 